import { useEffect } from 'react';

import type { TDealsExperimentGroup } from '../constants/dealsExperiment';
import { caShowCpdArticle } from '../utils/analytics/cpdDeals/trackingCpdDeals';

interface IUseTrackCpdArticleShowParams {
  enabled: boolean;
  postId: number | undefined;
  abGroup: TDealsExperimentGroup;
}

const isPageShowFromCache = (event: Event): boolean => 'persisted' in event && Boolean(event.persisted);

/**
 * На статьях эксперимента шлёт Magazine / Article / Show с duration в миллисекундах.
 * Считает только видимое время: фон вкладки и приложение на паузе.
 * Уход — размонтирование / смена статьи / pagehide; из кэша «Назад» — новый заход.
 */
export const useTrackCpdArticleShow = ({ enabled, postId, abGroup }: IUseTrackCpdArticleShowParams) => {
  useEffect(() => {
    if (!enabled || postId === undefined) {
      return;
    }

    let accumulatedMs = 0;
    let segmentStartedAt = document.visibilityState === 'visible' ? Date.now() : 0;
    let sent = false;

    const pause = () => {
      if (!segmentStartedAt) {
        return;
      }

      accumulatedMs += Date.now() - segmentStartedAt;
      segmentStartedAt = 0;
    };

    const resume = () => {
      if (document.visibilityState !== 'visible' || segmentStartedAt) {
        return;
      }

      segmentStartedAt = Date.now();
    };

    const send = () => {
      if (sent) {
        return;
      }

      sent = true;
      pause();
      caShowCpdArticle({
        duration: accumulatedMs,
        abGroup,
        articleId: postId,
      });
    };

    const resetVisit = () => {
      sent = false;
      accumulatedMs = 0;
      segmentStartedAt = 0;
      resume();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pause();

        return;
      }

      resume();
    };

    const onPageHide = () => {
      send();
    };

    const onPageShow = (event: Event) => {
      if (!isPageShowFromCache(event)) {
        return;
      }

      resetVisit();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
      send();
    };
  }, [abGroup, enabled, postId]);
};
